// imports
import { useState, useMemo, useCallback, useEffect } from 'react';

// local imports
import ReusableMenu from '../ReusableMenu';
import { useGetAwdConfigQuery, selectVariablesByModel } from '../../../../services/awdConfigApi';
import { initialWaterDataState } from '../../../../store/waterDataSlice';
import { getUrlParam } from '../../../../utils/getUrlParam';

interface VariableMenuProps {
  selectedSector: string;
  selectedModel: string | null;
  setSelectedVariable: (variable: string | null) => void;
}

export default function VariableMenu({
  selectedSector,
  selectedModel,
  setSelectedVariable,
}: VariableMenuProps) {
  const { data, isError, isFetching } = useGetAwdConfigQuery();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hasInitialized, setHasInitialized] = useState(false);

  const variableOptions = useMemo(() => {
    // For populating dropdown for when the variable API creates an error
    const defaultErrorOption = {
      key: 'error',
      value: 'No variables available - select a different model',
    };

    // For populating dropdown for when the variable API returns no data
    const noVariablesOption = {
      key: 'no-variables',
      value: 'No variables available - select a different model',
    };

    if (isError) {
      // If there was an error retrieving variables, populate menu with error message
      return [defaultErrorOption];
    }

    if (!data || !selectedModel) {
      return [noVariablesOption];
    }

    const variables = selectVariablesByModel(data.models, selectedModel);

    if (variables.length === 0) {
      return [noVariablesOption];
    }

    return variables;
  }, [isError, data, selectedModel]);

  const handleSelect = useCallback(
    (index: number) => {
      // If valid data are returned, set the variable selection and populate the date menus
      if (
        !isError &&
        variableOptions[index].key !== 'no-variables' &&
        data !== undefined
      ) {
        setSelectedIndex(index);
        const selectedVariable = variableOptions[index].key;
        // Update local state only - URL params updated when Update Map is clicked
        setSelectedVariable(selectedVariable);
      }
    },
    [isError, variableOptions, data, setSelectedVariable]
  );

  // Initialize from URL when data is available
  useEffect(() => {
    // Don't initialize until data has loaded
    if (isFetching || hasInitialized) {
      return;
    }

    if (variableOptions.length > 0) {
      const key = variableOptions[0].key;
      if (key === 'no-variables' || key === 'error') {
        setSelectedVariable(null);
        // Resetting selectedIndex to 0 as part of a guarded one-time initialization
        // block (hasInitialized prevents repeated execution). Keeps the UI consistent
        // when the variable list contains only a placeholder entry.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedIndex(0);
        setHasInitialized(true);
      } else {
        // Try to find variable from URL first
        const variableFromUrl = getUrlParam('variable');

        if (
          variableFromUrl &&
          variableOptions.some((opt) => opt.key === variableFromUrl)
        ) {
          // Use URL value if it exists and is valid
          const foundIndex = variableOptions.findIndex(
            (opt) => opt.key === variableFromUrl
          );
          setSelectedIndex(foundIndex);
          setSelectedVariable(variableFromUrl);
        } else {
          // Try Redux initial state default, then fall back to first option
          const defaultVariable = initialWaterDataState.selectedVariable;
          const defaultIndex = variableOptions.findIndex(
            (opt) => opt.key === defaultVariable
          );
          if (defaultIndex !== -1) {
            setSelectedIndex(defaultIndex);
            setSelectedVariable(defaultVariable);
          } else {
            setSelectedIndex(0);
            setSelectedVariable(variableOptions[0].key);
          }
        }
        setHasInitialized(true);
      }
    } else {
      setSelectedVariable(null);
      setSelectedIndex(0);
      setHasInitialized(true);
    }
  }, [
    variableOptions,
    isError,
    data,
    selectedModel,
    isFetching,
    hasInitialized,
    setSelectedVariable,
  ]);

  // Reset initialization flag when selectedModel changes
  useEffect(() => {
    // Resetting a boolean flag so the initialization effect re-runs after the
    // model changes and new variable data loads. This is a single-state update
    // that triggers one controlled re-initialization cycle.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHasInitialized(false);
  }, [selectedModel]);

  if (isFetching) return <div>Loading Variable Menu...</div>;

  // Update the 'learn more' link to reflect the selected variable
  const cloudfrontPrefix = import.meta.env.DEV ? `${import.meta.env.VITE_NWDC_S3_URL}` : import.meta.env.VITE_CLOUDFRONT_PROXY_PREFIX;
  const learnMoreLink = `${cloudfrontPrefix}/data-catalog/sector/${selectedSector}/model/${selectedModel}/variable/${variableOptions[selectedIndex]?.key}/#variableDescription`;

  return (
    <div>
      <ReusableMenu
        title="Variable"
        options={variableOptions}
        selectedIndex={selectedIndex}
        onSelect={(index) => handleSelect(index)}
        learnMoreLink={learnMoreLink}
        disabled={!!isError || variableOptions[0]?.key === 'no-variables'}
        menuItemTestId={(key) => `menu-item-${key}`}
      />
    </div>
  );
}
